// @ts-strict-ignore

export function applyDefaultValues<T>(inputValue: Partial<T>, defaultValue: T): T {
  let outputValue = structuredClone(inputValue);
  if (
    outputValue === undefined ||
    outputValue === null ||
    (outputValue instanceof Object && Object.keys(outputValue).length === 0)
  ) {
    return structuredClone(defaultValue);
  }

  if (defaultValue instanceof Object) {
    Object.entries(defaultValue).forEach(([key, value]) => {
      if (outputValue[key] === undefined || outputValue[key] === null) {
        outputValue[key] = structuredClone(value);
      } else if (value instanceof Array) {
        outputValue[key] = getArrayWithAppliedDefaultValues(outputValue[key], value);
      } else if (outputValue[key] instanceof Object && !(outputValue[key] instanceof Date)) {
        outputValue[key] = applyDefaultValues(outputValue[key], value);
      }
    });
  }
  if (outputValue instanceof Object && defaultValue !== null && defaultValue !== undefined) {
    Object.keys(outputValue).forEach((key) => {
      if (!Object.keys(defaultValue).some((k) => k === key)) {
        delete outputValue[key];
      }
    });
  }
  return outputValue as T;
}

/**
 * This function applies default values from the defaultArray onto the outputArray.
 * See the tests for "nested arrays" in apply-default-values.spec.ts for more details.
 *
 * @param outputArray Array with elements that should be updated with default values
 * @param defaultArray Array with default values. This will in most cases be an array with one element that is of the same type as the outputArray elements.
 * @returns Array with applied default values from the defaultArray onto the outputArray
 */
function getArrayWithAppliedDefaultValues<T>(outputArray: Partial<T>[], defaultArray: T[]): Partial<T>[] {
  if (!outputArray.length) {
    return defaultArray;
  }

  if (!defaultArray.length) {
    return outputArray;
  }

  const firstDefaultElement = defaultArray[0];

  return outputArray.map((outputArrayElement, index) => {
    const defaultArrayElement = defaultArray[index] ?? firstDefaultElement;
    return applyDefaultValues(outputArrayElement, defaultArrayElement);
  });
}
